/**
 * Variant URL Injection System
 * 
 * This system directly updates product card links with selected variant URLs
 * when variants are selected on product cards.
 */

class VariantUrlInjection {
  constructor() {
    this.init();
  }

  /**
   * Initialize the variant URL injection system
   */
  init() {
    // Listen for variant changes on product cards
    this.bindVariantChangeEvents();
    
    // Also listen for clicks on variant buttons/swatches
    this.bindVariantClickEvents();
  }

  /**
   * Bind events to variant changes
   */
  bindVariantChangeEvents() {
    // Use event delegation to catch all variant changes
    document.addEventListener('change', (event) => {
      const target = event.target;
      
      // Check if this is a variant selection change on a product card
      if (this.isVariantSelection(target)) {
        this.handleVariantChange(event);
      }
    });
  }

  /**
   * Bind events to variant clicks
   */
  bindVariantClickEvents() {
    // Listen for clicks on variant buttons and swatches
    document.addEventListener('click', (event) => {
      const target = event.target;
      
      // Check if this is a variant button/swatch click
      if (this.isVariantButton(target)) {
        // Use a small delay to ensure the selection is processed first
        setTimeout(() => {
          this.handleVariantChange(event);
        }, 50);
      }
    });
  }

  /**
   * Check if an element is a variant selection input
   */
  isVariantSelection(element) {
    return (
      element.tagName === 'INPUT' && 
      element.type === 'radio' && 
      element.dataset.optionName &&
      element.closest('.product-card')
    ) || (
      element.tagName === 'SELECT' && 
      element.closest('.product-card')
    );
  }

  /**
   * Check if an element is a variant button or swatch
   */
  isVariantButton(element) {
    return (
      element.closest('.variant-option__button-label') ||
      element.closest('.swatch') ||
      element.closest('[data-swatch]') ||
      element.closest('.variant-option')
    ) && element.closest('.product-card');
  }

  /**
   * Handle variant change events
   */
  handleVariantChange(event) {
    const variantPicker = event.target.closest('variant-picker, .variant-option, swatches-variant-picker-component');
    if (!variantPicker) return;

    const productCard = variantPicker.closest('.product-card');
    if (!productCard) return;

    // Get the selected variant URL
    const selectedVariantUrl = this.getSelectedVariantUrl(variantPicker);
    if (!selectedVariantUrl) return;

    // Update the product card link
    this.updateProductCardLink(productCard, selectedVariantUrl);
  }

  /**
   * Get the selected variant URL from a variant picker
   */
  getSelectedVariantUrl(variantPicker) {
    // Try to find the selected variant input/option
    const selectedInput = variantPicker.querySelector('input:checked, select option[selected]');
    if (!selectedInput) return null;

    // Get the variant URL from the selected element
    let variantUrl = null;

    // First priority: connected product URL (for combined listings)
    if (selectedInput.dataset.connectedProductUrl) {
      variantUrl = selectedInput.dataset.connectedProductUrl;
    } 
    // Second priority: variant-specific URL if available
    else if (selectedInput.dataset.variantUrl) {
      variantUrl = selectedInput.dataset.variantUrl;
    }
    // Third priority: build URL from variant ID
    else if (selectedInput.dataset.variantId) {
      const productCard = variantPicker.closest('.product-card');
      if (productCard) {
        const baseUrl = this.getBaseProductUrl(productCard);
        if (baseUrl) {
          variantUrl = `${baseUrl}?variant=${selectedInput.dataset.variantId}`;
        }
      }
    }

    return variantUrl;
  }

  /**
   * Try to get variant URL from various data attributes
   */
  getVariantUrlFromData(selectedInput) {
    // Check for variant-specific data attributes
    const variantId = selectedInput.dataset.variantId;
    const productCard = selectedInput.closest('.product-card');
    
    if (!variantId || !productCard) return null;

    // Try to get the base product URL
    const baseUrl = this.getBaseProductUrl(productCard);
    if (!baseUrl) return null;

    // Build the variant URL
    const variantUrl = `${baseUrl}?variant=${variantId}`;
    
    return variantUrl;
  }

  /**
   * Get the base product URL from a product card
   */
  getBaseProductUrl(productCard) {
    // Try to get from the product card link
    const productLink = productCard.querySelector('.product-card__link');
    if (productLink && productLink.href) {
      // Remove any existing variant parameters
      const url = new URL(productLink.href);
      url.searchParams.delete('variant');
      return url.toString();
    }

    // Try to get from data attributes
    const productId = productCard.dataset.productId;
    if (productId) {
      // This is a fallback - you might need to adjust based on your URL structure
      return `/products/${productId}`;
    }

    return null;
  }

  /**
   * Update the product card link with the selected variant URL
   */
  updateProductCardLink(productCard, variantUrl) {
    // Find the main product card link
    const productLink = productCard.querySelector('.product-card__link');
    if (!productLink) return;

    // Update the href attribute
    productLink.href = variantUrl;

    // Also update any other product links in the card
    const otherProductLinks = productCard.querySelectorAll('a[href*="/products/"]:not(.product-card__link)');
    otherProductLinks.forEach(link => {
      // Only update if it's a product link (not external links)
      if (link.href.includes('/products/')) {
        link.href = variantUrl;
      }
    });

    // Update any buttons with product URLs
    const productButtons = productCard.querySelectorAll('[data-product-url]');
    productButtons.forEach(button => {
      button.dataset.productUrl = variantUrl;
    });

  }

}

// Initialize the variant URL injection system when the script loads
const variantUrlInjection = new VariantUrlInjection();

// Export for potential use in other modules
window.VariantUrlInjection = VariantUrlInjection;
